import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { Modal } from '../ui/kit.js';
import { useToast } from '../ui/actions/toast.js';
import { QuickMenu } from '../ui/actions/quick-menu.js';
import { ScenePicker, sceneOf } from '../ui/actions/scenes.js';
import { useBuilding } from '../state/building.js';
import { SCREENS_FOR } from '../screens/registry.js';

import { ReactComponent as Menu } from "../media/images/svg/menu.svg";

const EASE = [0.16, 1, 0.3, 1];

/* How long a scene takes to "set". The backdrop's own shift in
   design/actions.css runs for the same 1.2s, so the card's bar and the
   room behind the sheet finish together. */
const SCENE_MS = 1200;

/**
 * The top bar. The building's name sits where the System control used to,
 * and Scenes keeps its place; the clock is new, because a wall-mounted
 * screen that shows the time is useful even when nobody is using it.
 *
 * Scenes and the menu glyph used to be inert. Scenes now opens the scene
 * picker and the glyph opens a quick menu of the whole side; both are
 * sheets from ui/kit.js, and what they change is confirmed by a toast.
 */
/* The properties that link to this dashboard. Recognising the referrer
   lets the exit say where it goes rather than just "close". */
const KNOWN = [
    { host: 'smart-tech.solutions',          name: 'Smart Technology Solutions' },
    { host: 'smartelectric.solutions',       name: 'Smart Electric Solutions' },
    { host: 'smartconstruction.solutions',   name: 'Smart Construction Solutions' },
    { host: 'thesmartsolutionsgroup.net',    name: 'The Smart Solutions Group' }
];

function TopBar({ mode = 'resident', onModeChange = () => {} }) {
    const [now, setNow] = useState(() => new Date());
    const [exit, setExit] = useState(null);
    const { go } = useBuilding();
    const reduced = useReducedMotion();

    const [menuOpen, setMenuOpen] = useState(false);
    const [scenesOpen, setScenesOpen] = useState(false);
    const [scene, setScene] = useState(null);
    const [applying, setApplying] = useState(null);
    const [toastNode, toast] = useToast();
    const sceneTimer = useRef(null);

    /* A scene is set on the root element so the backdrop, which lives
       outside the bar, can follow it from CSS alone. It is taken off
       again if the bar ever unmounts, so nothing outlives the kiosk. */
    useEffect(() => () => {
        clearTimeout(sceneTimer.current);
        delete document.documentElement.dataset.scene;
    }, []);

    const applyScene = useCallback(key => {
        const chosen = sceneOf(key);
        if (!chosen || applying) return;

        /* The backdrop starts drifting now, under the open sheet, so the
           change is visible while the card's bar fills. */
        document.documentElement.dataset.scene = key;
        setApplying(key);

        sceneTimer.current = setTimeout(() => {
            setScene(key);
            setApplying(null);
            setScenesOpen(false);
            setMenuOpen(false);
            toast(`${chosen.name} scene on. ${chosen.summary}.`, { swatch: chosen.swatch });
        }, reduced ? 0 : SCENE_MS);
    }, [applying, reduced, toast]);

    const active = sceneOf(scene);

    /* Where the reader came from.
     *
     * `?from=` is the reliable half: the company sites put their current
     * address there when they link here. document.referrer is only a
     * fallback, and a weak one: those links carry rel="noreferrer", and
     * even without it a cross-origin referrer is trimmed to the bare
     * origin, so it can say which site but not which page.
     *
     * Either way the destination is checked against the list above. An
     * address arriving in a query string is reader-supplied input, and
     * this control must not become a way to bounce a kiosk to anywhere on
     * the internet.
     *
     * Opened directly (a kiosk booting to this URL, or a bookmark),
     * there is nothing to go back to and no control is rendered. */
    useEffect(() => {
        const permitted = (raw) => {
            if (!raw) return null;

            try {
                const url = new URL(raw, window.location.origin);

                if (!/^https?:$/.test(url.protocol)) return null;
                if (url.origin === window.location.origin) return null;

                const host = url.hostname.replace(/^www\./, '');
                const known = KNOWN.find(k => host === k.host);

                return known ? { href: url.toString(), name: known.name } : null;
            } catch (error) {
                return null;
            }
        };

        const from = new URLSearchParams(window.location.search).get('from');

        setExit(permitted(from) || permitted(document.referrer));
    }, []);

    useEffect(() => {
        /* Tick on the minute rather than the second: this display is read
           from across a lobby, and a second hand would keep the compositor
           awake all day for something nobody can see. */
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    const time = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const date = now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

    return (
        <motion.header
            className="bar"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: EASE }}
        >
            {/* Placeholder until each building supplies its own name. */}
            <span className="bar__building">Your Building Name Here</span>

            <div className="bar__brand">
                {/* Was a decorative glyph. It opens the whole side at once,
                    and wears the active scene's light as a dot, since on a
                    phone this is the only way to the scenes. */}
                <button
                    type="button"
                    className="bar__menu"
                    aria-label="Open the menu"
                    aria-haspopup="dialog"
                    onClick={() => setMenuOpen(true)}
                >
                    <Menu aria-hidden="true" />
                    {active && <span className="bar__menu-dot" style={{ background: active.swatch }} aria-hidden="true" />}
                </button>
                <span className="bar__clock">
                    <span className="bar__time">{time}</span>
                    <span className="bar__date">{date}</span>
                </span>
            </div>

            <div className="bar__end">
                {/* Resident and staff are two views of one building. On a
                    real property they are separate logins; the demo puts
                    both a tap apart so the hand-off between them shows. */}
                <div className="bar__mode" role="group" aria-label="View as">
                    {[['resident', 'Resident'], ['staff', 'Staff']].map(([value, label]) => (
                        <button
                            type="button"
                            key={value}
                            aria-pressed={mode === value}
                            onClick={() => onModeChange(value)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className="bar__button"
                    aria-haspopup="dialog"
                    onClick={() => setScenesOpen(true)}
                >
                    {active && <span className="bar__scene-dot" style={{ background: active.swatch }} aria-hidden="true" />}
                    Scenes
                    {active && <span className="bar__scene-name">{active.name}</span>}
                </button>

                {exit && (
                    <a
                        className="bar__exit"
                        href={exit.href}
                        aria-label={exit.name ? `Close and return to ${exit.name}` : 'Close and go back'}
                        title={exit.name ? `Back to ${exit.name}` : 'Back'}
                    >
                        <span aria-hidden="true">&times;</span>
                    </a>
                )}
            </div>

            <Modal
                open={scenesOpen}
                onClose={() => setScenesOpen(false)}
                size="lg"
                eyebrow="Scenes"
                title="Set the mood in one tap"
                dismissable={!applying}
            >
                <p className="scenes__lead">
                    Lights, shades, climate and music change together.
                    {active ? ` ${active.name} is on now.` : ' Choose a scene to set them all.'}
                </p>
                <ScenePicker active={scene} applying={applying} onApply={applyScene} />
            </Modal>

            <QuickMenu
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                mode={mode}
                items={SCREENS_FOR[mode] || []}
                onGo={go}
                /* Changing to the side already shown is how home.js says
                   "go to this side's home"; the resident welcome screen is
                   not a destination `go` will accept. */
                onHome={() => onModeChange(mode)}
                onSwitch={() => onModeChange(mode === 'staff' ? 'resident' : 'staff')}
                scene={scene}
                applying={applying}
                onScene={applyScene}
            />

            {toastNode}
        </motion.header>
    );
}

export default TopBar;
